/**
 * Packet-loss concealment for the listener.
 *
 * Neither browser Opus path exposes libopus's own PLC: WebCodecs `AudioDecoder`
 * has no "decode a missing packet" call at all, and `opus-decoder` only takes real
 * packets. So concealment happens on decoded audio instead — the last good frame is
 * repeated under a decaying ramp, which sounds like a short smear rather than the
 * click a hole of silence would produce. Past `MAX_PLC_FRAMES` the caller resets,
 * which is where this stops being convincing anyway.
 */

/** Amplitude left after each concealed frame. */
const DECAY = 0.55;

export function concealFrame(
  previous: readonly Float32Array[] | null,
  index: number,
  fallbackSamples: number,
  fallbackChannels: number,
): Float32Array[] {
  if (!previous || previous.length === 0 || (previous[0]?.length ?? 0) === 0) {
    const planes: Float32Array[] = [];
    for (let c = 0; c < Math.max(1, fallbackChannels); c++) {
      planes.push(new Float32Array(Math.max(0, fallbackSamples)));
    }
    return planes;
  }

  const startGain = Math.pow(DECAY, index);
  const endGain = Math.pow(DECAY, index + 1);
  const length = previous[0]?.length ?? 0;

  return previous.map((plane) => {
    const out = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      const t = length === 1 ? 1 : i / (length - 1);
      out[i] = (plane[i] ?? 0) * (startGain + (endGain - startGain) * t);
    }
    return out;
  });
}
